import React, { useState, useCallback } from 'react';
import Swal from 'sweetalert2';
import CreditNoteModule from './CreditNoteModule';
import AddCreditNoteForm from './AddCreditNoteForm';
import ShowCreditNoteView from './ShowCreditNoteView';
import CreditNoteSettings from './setting/CreditNoteSettings';
import { updateCreditNoteStatusById, getCreditNoteDetailById, saveApprovalSetting } from '../../utils/lead';
import { getSession } from '../../getSession';

const CreditNoteModuleWrapper = () => {
    // view: 'list' | 'add' | 'show' | 'edit' | 'settings'
    const [view, setView] = useState('list');
    const [selectedCN, setSelectedCN] = useState(null);  // row from list (for show id & edit data)
    const [refreshKey, setRefreshKey] = useState(0);     // bump to refresh list after status change
    const { userId } = getSession();

    const refreshList = () => setRefreshKey(k => k + 1);

    /* ── ADD ── */
    const handleAddClick = () => {
        setSelectedCN(null);
        setView('add');
    };

    /* ── SETTINGS ── */
    const handleSettingsClick = () => {
        setSelectedCN(null);
        setView('settings');
    };

    /* ── SHOW ── */
    const handleShowClick = useCallback((cn) => {
        setSelectedCN(cn);
        setView('show');
    }, []);

    /* ── EDIT ── */
    const handleEditClick = useCallback(async (cn) => {
        try {
            const detail = await getCreditNoteDetailById({
                Id: cn.Id,
                TimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            setSelectedCN(detail || cn);
        } catch {
            setSelectedCN(cn);
        }
        setView('edit');
    }, []);

    /* ── CANCEL ── */
    const handleCancelClick = useCallback(async (cn) => {
        const result = await Swal.fire({
            title: 'Cancel Credit Note?',
            text: `Are you sure you want to cancel credit note ${cn.CreditNoteNumber}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Cancel it',
            cancelButtonText: 'No',
            confirmButtonColor: '#ef4444',
        });
        if (!result.isConfirmed) return;
        try {
            await updateCreditNoteStatusById({
                Ids: cn.Id,
                Mode: 'cancelled',
                UserId: userId
            });
            Swal.fire('Done', 'Credit Note marked as cancelled.', 'success');
            refreshList();
        } catch (err) {
            Swal.fire('Error', 'Unable to cancel. Please try again.', 'error');
        }
    }, [userId]);

    /* ── DELETE ── */
    const handleDeleteClick = useCallback(async (cn) => {
        const result = await Swal.fire({
            title: 'Delete Credit Note?',
            text: `This will permanently delete ${cn.CreditNoteNumber}. Continue?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'No',
            confirmButtonColor: '#ef4444',
        });
        if (!result.isConfirmed) return;
        try {
            await updateCreditNoteStatusById({
                Ids: cn.Id,
                Mode: 'deleted',
                UserId: userId
            });
            Swal.fire('Deleted', 'Credit Note deleted successfully.', 'success');
            refreshList();
        } catch (err) {
            Swal.fire('Error', 'Unable to delete. Please try again.', 'error');
        }
    }, [userId]);

    /* ── REQUEST APPROVAL ── */
    const handleRequestApproval = useCallback(async (cn) => {
        try {
            const response = await saveApprovalSetting({
                UserId: userId,
                EntityName: 'CreditNote',
                EntityId: cn.Id
            });
            if (response) {
                Swal.fire('Success', 'Sent for approval successfully', 'success');
                refreshList();
            }
        } catch (err) {
            Swal.fire('Error', 'Unable to send for approval. Please try again.', 'error');
        }
    }, [userId]);

    const handleBackToList = () => {
        setSelectedCN(null);
        setView('list');
        refreshList();
    };

    return (
        <div className="credit-note-wrapper" style={{ position: 'relative' }}>
            {/* List always rendered beneath overlays */}
            <CreditNoteModule
                key={refreshKey}
                onAddClick={handleAddClick}
                onSettingsClick={handleSettingsClick}
                onShowClick={handleShowClick}
                onEditClick={handleEditClick}
                onCancelClick={handleCancelClick}
                onDeleteClick={handleDeleteClick}
                onRequestApprovalClick={handleRequestApproval}
            />

            {/* SHOW overlay */}
            {view === 'show' && selectedCN && (
                <div className="credit-note-overlay-container">
                    <div className="credit-note-overlay-backdrop" onClick={handleBackToList} />
                    <div className="credit-note-form-panel">
                        <ShowCreditNoteView
                            creditNoteId={selectedCN.Id}
                            onBack={handleBackToList}
                            onRefresh={refreshList}
                        />
                    </div>
                </div>
            )}

            {/* ADD overlay */}
            {view === 'add' && (
                <div className="credit-note-overlay-container">
                    <div className="credit-note-overlay-backdrop" onClick={handleBackToList} />
                    <div className="credit-note-form-panel">
                        <AddCreditNoteForm onBack={handleBackToList} />
                    </div>
                </div>
            )}

            {/* EDIT overlay */}
            {view === 'edit' && selectedCN && (
                <div className="credit-note-overlay-container">
                    <div className="credit-note-overlay-backdrop" onClick={handleBackToList} />
                    <div className="credit-note-form-panel">
                        <AddCreditNoteForm
                            onBack={handleBackToList}
                            editData={selectedCN}
                        />
                    </div>
                </div>
            )}

            {/* SETTINGS overlay */}
            {view === 'settings' && (
                <div className="credit-note-overlay-container">
                    <div className="credit-note-overlay-backdrop" onClick={handleBackToList} />
                    <div className="credit-note-form-panel" style={{ width: '85%' }}>
                        <CreditNoteSettings onBack={handleBackToList} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreditNoteModuleWrapper;
